<template>
  <TimelineCard>
    <template #header>
      <div class="flex items-center gap-2 flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          <h3 class="font-semibold text-slate-900 text-sm m-0 leading-6 sm:text-[0.8125rem]">
            {{ getFormattedTitle(note.type) }}
          </h3>

          <TimelineTag
            :icon="getTypeIcon(note.type)"
            :label="NoteTypeName[note.type]"
            :variant="getTypeVariant(note.type)"
          />

          <TimelineTag
            v-if="note.showOnVendorReport"
            label="Vendor Report"
            variant="amber"
          />
        </div>
      </div>

      <div class="flex items-center gap-1 flex-shrink-0">
        <Button
          v-if="!note.isPinned && showActions"
          variant="text"
          label="Pin"
          size="small"
          @click="changeNotePinStatus(note.id, !note.isPinned)"
        >
          <template #icon>
            <Icon
              name="ph:push-pin"
              size="14"
            />
          </template>
        </Button>
        <Button
          v-else-if="showActions"
          variant="text"
          label="Unpin"
          size="small"
          class="text-[#F59E0B]"
          @click="changeNotePinStatus(note.id, !note.isPinned)"
        >
          <template #icon>
            <Icon
              name="ph:push-pin-slash-duotone"
              size="14"
            />
          </template>
        </Button>
        <NoteEdit
          v-if="showActions"
          v-model:noteId="note.id"
          @refresh="emit('refresh')"
        />
        <NoteDelete
          v-if="showActions"
          v-model:noteId="note.id"
          @refresh="emit('refresh')"
        />
      </div>
    </template>

    <template #content>
      <div class="mt-2 text-[0.8125rem] leading-relaxed text-slate-700 sm:text-xs">
        <div
          v-if="shouldShowExpand"
          class="relative"
        >
          <div
            ref="contentRef"
            class="transition-[max-height] duration-300 ease-in-out"
            :class="{ 'max-h-24 overflow-hidden relative': !isExpanded }"
          >
            <WidgetsRichTextEditor
              :data="noteContent"
              :isEditing="false"
            />
          </div>
          <div
            v-if="!isExpanded"
            class="absolute bottom-8 left-0 right-0 h-8 bg-gradient-to-b from-transparent to-white/95 pointer-events-none z-10"
          />
          <button
            class="flex items-center gap-1 mt-2 text-xs text-blue-600 font-medium bg-transparent border-0 cursor-pointer py-1 px-0 transition-colors duration-200 ease-in-out hover:text-blue-700"
            @click="expandSection"
          >
            <Icon
              :name="isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'"
              size="14"
            />
            <span>{{ isExpanded ? 'Show less' : 'Show more' }}</span>
          </button>
        </div>
        <div v-else>
          <WidgetsRichTextEditor
            :data="noteContent"
            :isEditing="false"
          />
        </div>
      </div>

      <div
        v-if="noteAttachments && noteAttachments.length > 0"
        class="mt-3 pt-3 border-t border-slate-100"
      >
        <div class="flex items-center gap-2 flex-wrap">
          <Icon
            name="mdi:paperclip"
            size="12"
            class="text-slate-400"
          />
          <div class="flex items-center gap-2 flex-wrap">
            <a
              v-for="(item, index) in noteAttachments"
              :key="index"
              :href="(item as any)?.publicUrl"
              target="_blank"
              class="inline-flex items-center gap-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded-md text-slate-600 no-underline text-xs transition-all duration-200 ease-in-out hover:bg-slate-100 hover:border-slate-300 hover:text-slate-700"
            >
              <Icon
                name="mdi:file-outline"
                size="12"
              />
              <span>{{ (item as any)?.name }}</span>
            </a>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <TimelineFooter
        :author-name="(note as any).UserWorkSpace?.name || 'System'"
        :contact="(note as any)?.Contact ? {
          id: note.contactId!,
          firstName: (note as any).Contact.firstName,
          lastName: (note as any).Contact.lastName,
        } : undefined"
        :business="(note as any)?.Business ? {
          id: note.businessId!,
          name: (note as any).Business.name,
        } : undefined"
        :contract="(note as any)?.Contract ? {
          id: note.contractId!,
          name: (note as any).Contract.Business.name,
        } : undefined"
        :created-at="note.createdAt"
      />
    </template>
  </TimelineCard>
</template>

<script setup lang="ts">
import type { Note } from '@prisma/client'
import { NoteType } from '@prisma/client'
import { NoteTypeIcon, NoteTypeColor, NoteTitleFormat, NoteTypeName } from '../ActivityFeed/types'
import TimelineCard from '../ActivityFeed/Base/TimelineCard.vue'
import TimelineTag from '../ActivityFeed/Base/TimelineTag.vue'
import TimelineFooter from '../ActivityFeed/Base/TimelineFooter.vue'

interface Props {
  note: Note
  showActions?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['refresh'])
const toast = useToast()
const { updateNotePinStatus } = useNotes()

const note = props.note

const getTypeIcon = (noteType: NoteType): string => {
  if (noteType in NoteTypeIcon) {
    return NoteTypeIcon[noteType]
  }
  return NoteTypeIcon[NoteType.Note]
}

type VariantType = 'blue' | 'cyan' | 'purple' | 'yellow' | 'orange' | 'lime' | 'indigo' | 'green' | 'red' | 'emerald' | 'amber' | 'gray'

const getTypeVariant = (noteType: NoteType): VariantType => {
  if (noteType in NoteTypeColor) {
    const color = NoteTypeColor[noteType]

    const validVariants: readonly VariantType[] = ['blue', 'cyan', 'purple', 'yellow', 'orange', 'lime', 'indigo', 'green', 'red', 'emerald', 'amber', 'gray'] as const
    if (validVariants.includes(color as VariantType)) {
      return color as VariantType
    }
  }
  return 'gray' as const
}

const getFormattedTitle = (noteType: NoteType): string => {
  if (noteType in NoteTitleFormat) {
    const noteWithUser = note as Note & {
      UserWorkSpace?: {
        name: string | null
        User?: {
          name: string | null
        }
      }
    }
    const userName = noteWithUser.UserWorkSpace?.name || 'System'
    return NoteTitleFormat[noteType].replace('[USER_NAME]', userName)
  }

  return noteType
}

const noteContent = computed(() => note.content)

const noteAttachments = computed(() => {
  if (!note.attachments) return null
  if (Array.isArray(note.attachments)) {
    return note.attachments
  }
  return null
})

const getPlainTextLength = (html: string): number => {
  if (!html) return 0
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.textContent?.trim().length || 0
}

const shouldShowExpand = computed(() => {
  const plainTextLength = getPlainTextLength(noteContent.value)
  return plainTextLength > 200
})

const isExpanded = ref(false)
const contentRef = ref<HTMLElement | null>(null)

const expandSection = () => {
  isExpanded.value = !isExpanded.value
}

const changeNotePinStatus = async (noteId: string, status: boolean) => {
  try {
    const { error } = await updateNotePinStatus(noteId, status)

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update note pin status',
        life: 3000,
      })
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: status ? 'Note pinned successfully' : 'Note unpinned successfully',
      life: 3000,
    })

    emit('refresh')
  }
  catch (error) {
    console.error('Error updating note pin status:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update note pin status',
      life: 3000,
    })
  }
}
</script>
